// Tabs.js
import React, { useEffect, useMemo, useRef, useState } from "react";

export default function Tabs({ activeTab, setActiveTab }) {
  const tabs = useMemo(
    () => [
      { id: 1, label: "3D Sign 🪧️" },
      { id: 2, label: "Keychain/Ornament 🔖️" },
      { id: 3, label: "Coaster 🍵️" },
      { id: 4, label: "Lightbox 💡️" },
      { id: 5, label: "Desk Organizer 📝️" },
      { id: 6, label: "Bookmark 📚️" },
      { id: 7, label: "Cake Topper 🎂️" },
      { id: 8, label: "Custom Phone Stand (TBD)" },
      { id: 9, label: "Custom Standee (TBD)" },
      { id: 11, label: "My Storefront 🏪️" },
      { id: 12, label: "My Projects 🖌️" },
    ],
    []
  );

  // Tabs that are clickable (enabled)
  const enabledTabs = useMemo(() => [1, 2, 3, 4, 5, 6, 7, 11, 12], []);

  // Product tabs: all tabs except 11 & 12
  const productTabs = useMemo(
    () => tabs.filter((t) => t.id !== 11 && t.id !== 12),
    [tabs]
  );

  // Bottom tabs: only 11 & 12
  const bottomTabs = useMemo(
    () => tabs.filter((t) => t.id === 11 || t.id === 12),
    [tabs]
  );

  // Portrait vs Landscape
  const [isPortrait, setIsPortrait] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerHeight > window.innerWidth;
  });

  useEffect(() => {
    const onResize = () => setIsPortrait(window.innerHeight > window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Scroller refs / flags
  const scrollerRef = useRef(null);

  const isInteractingRef = useRef(false);
  const isDraggingRef = useRef(false);
  const didDragRef = useRef(false);
  const dragActivatedRef = useRef(false);

  const dragStartXRef = useRef(0);
  const scrollStartLeftRef = useRef(0);

  const rafRef = useRef(null);
  const lastTsRef = useRef(null);

  // -----------------------------
  // Tab corner radius ("fillet")
  // -----------------------------
  const TAB_RADIUS_PX = 999; // tune 12–18, or set 999 for pill tabs

  // -----------------------------
  // Hover tooltip bubble (cursor-follow)
  // -----------------------------
  const [showBubble, setShowBubble] = useState(false);
  const [bubblePos, setBubblePos] = useState({ x: 0, y: 0 });

  const bubbleRafRef = useRef(null);
  const lastMouseRef = useRef({ x: 0, y: 0 });

  const updateBubblePos = (clientX, clientY) => {
    lastMouseRef.current = { x: clientX, y: clientY };
    if (bubbleRafRef.current) return;

    bubbleRafRef.current = requestAnimationFrame(() => {
      bubbleRafRef.current = null;
      const { x, y } = lastMouseRef.current;
      // Offset so it doesn't cover the cursor
      setBubblePos({ x: x + 14, y: y + 18 });
    });
  };

  useEffect(() => {
    return () => {
      if (bubbleRafRef.current) cancelAnimationFrame(bubbleRafRef.current);
      bubbleRafRef.current = null;
    };
  }, []);

  // -----------------------------
  // Auto-scroll (idle bounce)
  // -----------------------------
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    const SPEED_PX_PER_SEC = 24;
    let direction = 1;
    let interactionTimeout = null;

    const markInteracting = () => {
      isInteractingRef.current = true;
      if (interactionTimeout) clearTimeout(interactionTimeout);
      interactionTimeout = setTimeout(() => {
        isInteractingRef.current = false;
        lastTsRef.current = null;
      }, 900);
    };

    const step = (ts) => {
      const node = scrollerRef.current;
      if (!node) return;

      if (lastTsRef.current == null) lastTsRef.current = ts;
      const dt = (ts - lastTsRef.current) / 1000;
      lastTsRef.current = ts;

      const maxScrollLeft = Math.max(0, node.scrollWidth - node.clientWidth);
      const canScroll = maxScrollLeft > 0;

      if (canScroll && !isInteractingRef.current && !isDraggingRef.current) {
        let next = node.scrollLeft + SPEED_PX_PER_SEC * dt * direction;

        if (next >= maxScrollLeft) {
          next = maxScrollLeft;
          direction = -1;
        } else if (next <= 0) {
          next = 0;
          direction = 1;
        }

        node.scrollLeft = next;
      }

      rafRef.current = requestAnimationFrame(step);
    };

    rafRef.current = requestAnimationFrame(step);

    // Pause auto-scroll on real user interactions (NOT on "scroll")
    const onWheel = () => markInteracting();
    const onPointerDown = () => markInteracting();
    const onTouchStart = () => markInteracting();

    el.addEventListener("wheel", onWheel, { passive: true });
    el.addEventListener("pointerdown", onPointerDown, { passive: true });
    el.addEventListener("touchstart", onTouchStart, { passive: true });

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      lastTsRef.current = null;
      if (interactionTimeout) clearTimeout(interactionTimeout);

      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("touchstart", onTouchStart);
    };
  }, []);

  // -----------------------------
  // Desktop click-and-drag scroll
  // - DOES NOT disable clicks unless the user actually drags past threshold
  // - Uses capture so mousedown works even over buttons
  // -----------------------------
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    let resumeTimeout = null;

    const resumeAutoScrollSoon = () => {
      if (resumeTimeout) clearTimeout(resumeTimeout);
      resumeTimeout = setTimeout(() => {
        isInteractingRef.current = false;
        lastTsRef.current = null;
      }, 900);
    };

    const DRAG_THRESHOLD_PX = 5;

    const onMouseDownCapture = (e) => {
      if (e.button !== 0) return;

      isDraggingRef.current = true;
      didDragRef.current = false;
      dragActivatedRef.current = false;

      isInteractingRef.current = true;

      dragStartXRef.current = e.clientX;
      scrollStartLeftRef.current = el.scrollLeft;

      // Do NOT preventDefault here; otherwise buttons never click.
      // We only preventDefault once the drag crosses threshold.
    };

    const onMouseMove = (e) => {
      if (!isDraggingRef.current) return;

      const dx = e.clientX - dragStartXRef.current;

      // Activate drag only after threshold
      if (!dragActivatedRef.current && Math.abs(dx) >= DRAG_THRESHOLD_PX) {
        dragActivatedRef.current = true;
        didDragRef.current = true;

        el.classList.add("tabs-dragging");
        el.style.cursor = "grabbing";
        el.style.userSelect = "none";
      }

      if (!dragActivatedRef.current) return;

      // Actual drag scrolling
      el.scrollLeft = scrollStartLeftRef.current - dx;

      // Prevent selection / native drag once we're truly dragging
      e.preventDefault();
    };

    const stopMouseDrag = () => {
      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;

      el.classList.remove("tabs-dragging");
      el.style.cursor = "grab";
      el.style.userSelect = "auto";

      // Keep didDragRef true briefly so the click that follows mouseup is ignored
      if (didDragRef.current) {
        setTimeout(() => {
          didDragRef.current = false;
        }, 0);
      }

      resumeAutoScrollSoon();
    };

    el.style.cursor = "grab";

    // Capture mousedown so it triggers even if user starts on a tab button
    el.addEventListener("mousedown", onMouseDownCapture, true);
    window.addEventListener("mousemove", onMouseMove, { passive: false });
    window.addEventListener("mouseup", stopMouseDrag);
    window.addEventListener("blur", stopMouseDrag);
    el.addEventListener("mouseleave", stopMouseDrag);

    return () => {
      if (resumeTimeout) clearTimeout(resumeTimeout);
      el.removeEventListener("mousedown", onMouseDownCapture, true);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", stopMouseDrag);
      window.removeEventListener("blur", stopMouseDrag);
      el.removeEventListener("mouseleave", stopMouseDrag);
    };
  }, []);

  // -----------------------------
  // Mobile touch drag (kept)
  // -----------------------------
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    let resumeTimeout = null;

    const resumeAutoScrollSoon = () => {
      if (resumeTimeout) clearTimeout(resumeTimeout);
      resumeTimeout = setTimeout(() => {
        isInteractingRef.current = false;
        lastTsRef.current = null;
      }, 900);
    };

    const onTouchStart = (e) => {
      if (!e.touches || e.touches.length !== 1) return;

      isDraggingRef.current = true;
      isInteractingRef.current = true;

      dragStartXRef.current = e.touches[0].clientX;
      scrollStartLeftRef.current = el.scrollLeft;
    };

    const onTouchMove = (e) => {
      if (!isDraggingRef.current) return;
      if (!e.touches || e.touches.length !== 1) return;

      const dx = e.touches[0].clientX - dragStartXRef.current;
      el.scrollLeft = scrollStartLeftRef.current - dx;
    };

    const onTouchEnd = () => {
      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;
      resumeAutoScrollSoon();
    };

    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: true });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    el.addEventListener("touchcancel", onTouchEnd, { passive: true });

    return () => {
      if (resumeTimeout) clearTimeout(resumeTimeout);
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
    };
  }, []);

  const renderTabButton = ({ id, label }) => {
    const isEnabled = enabledTabs.includes(id);
    const isActive = id === activeTab;

    return (
      <button
        key={id}
        onMouseEnter={(e) => {
          setShowBubble(true);
          updateBubblePos(e.clientX, e.clientY);
        }}
        onMouseMove={(e) => {
          if (!showBubble) setShowBubble(true);
          updateBubblePos(e.clientX, e.clientY);
        }}
        onMouseLeave={() => setShowBubble(false)}
        onClick={(e) => {
          // If the user dragged, ignore the click that may fire after mouseup
          if (didDragRef.current) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }
          if (isEnabled) setActiveTab(id);
        }}
        disabled={!isEnabled}
        style={{
          flex: "0 0 auto",
          minWidth: "120px",
          padding: "8px 16px",
          marginRight: "6px",
          marginBottom: "6px",
          fontSize: "2vh",
          fontWeight: isActive ? "600" : "400",
          backgroundColor: isActive ? "white" : "#f1f5f9",
          border: isActive ? "1px solid #cbd5e1" : "none",
          borderBottom: isActive ? "none" : "1px solid #cbd5e1",
          borderTopLeftRadius: `${TAB_RADIUS_PX}px`,
          borderTopRightRadius: `${TAB_RADIUS_PX}px`,
          borderBottomLeftRadius: `${TAB_RADIUS_PX}px`,
          borderBottomRightRadius: `${TAB_RADIUS_PX}px`,
          color: isActive ? "#000" : "#6b7280",
          cursor: isEnabled ? "pointer" : "not-allowed",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </button>
    );
  };

  // Shared scroller style (hidden scrollbar)
  const scrollerStyle = {
    display: "block",
    width: "100%",
    maxWidth: "100%",
    overflowX: "auto",
    overflowY: "hidden",
    WebkitOverflowScrolling: "touch",
    scrollbarWidth: "none", // Firefox
    msOverflowStyle: "none", // legacy
    boxSizing: "border-box",
  };

  return (
    <div
      style={{
        backgroundColor: "#e2e8f0",
        borderBottom: "1px solid #cbd5e1",
        padding: "4px 8px",
        width: "100%",
        maxWidth: "100vw",
        minWidth: 0,
        boxSizing: "border-box",
        overflowX: "hidden",
        position: "relative",
      }}
    >
      <style>{`
        /* Hide scrollbar for WebKit */
        [data-hide-scrollbar]::-webkit-scrollbar { display: none; }

        /*
          Only during an active drag (after threshold) we disable child pointer events
          so selection/click doesn't fight the drag.
        */
        [data-tabs-strip].tabs-dragging * { pointer-events: none; }
      `}</style>

      {/* Cursor-follow bubble */}
      {showBubble && (
        <div
          style={{
            position: "fixed",
            left: `${bubblePos.x}px`,
            top: `${bubblePos.y}px`,
            zIndex: 999999,
            pointerEvents: "none",
            backgroundColor: "rgba(17, 24, 39, 0.95)", // slate-900
            color: "white",
            padding: "8px 10px",
            borderRadius: "14px",
            fontSize: "12px",
            fontWeight: 600,
            boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
            whiteSpace: "nowrap",
            border: "1px solid rgba(255,255,255,0.15)",
            transform: "translateZ(0)",
          }}
        >
          Hold &amp; move to Slide the Bar!
        </div>
      )}

      {/* HEADER + SCROLLER:
          - Portrait: header on its own line, centered
          - Landscape: header inline with scroller, left aligned
      */}
      {isPortrait ? (
        <>
          {/* Line 1: centered header */}
          <div
            style={{
              width: "100%",
              textAlign: "center",
              padding: "6px 4px 2px 4px",
              fontSize: "2vh",
              fontWeight: 600,
              color: "#111827",
              whiteSpace: "nowrap",
            }}
          >
            Custom 3D Products:
          </div>

          {/* Line 2: scrolling tabs */}
          <div
            style={{
              width: "100%",
              overflow: "hidden",
              minWidth: 0,
              marginBottom: "6px",
            }}
          >
            <div
              ref={scrollerRef}
              data-tabs-strip
              data-hide-scrollbar
              style={scrollerStyle}
            >
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "flex-end",
                  whiteSpace: "nowrap",
                  minWidth: "max-content",
                }}
              >
                {productTabs.map((t) => renderTabButton(t))}
              </div>
            </div>
          </div>
        </>
      ) : (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            maxWidth: "100vw",
            boxSizing: "border-box",
            marginBottom: "6px",
            minWidth: 0,
            overflow: "hidden",
          }}
        >
          {/* Inline header (not centered) */}
          <div
            style={{
              flex: "0 0 auto",
              padding: "8px 10px 8px 4px",
              fontSize: "2vh",
              fontWeight: 600,
              color: "#111827",
              whiteSpace: "nowrap",
            }}
          >
            Custom 3D Products:
          </div>

          {/* Scrolling tabs */}
          <div
            style={{
              flex: "1 1 0%",
              minWidth: 0,
              maxWidth: "100%",
              overflow: "hidden",
            }}
          >
            <div
              ref={scrollerRef}
              data-tabs-strip
              data-hide-scrollbar
              style={scrollerStyle}
            >
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "flex-end",
                  whiteSpace: "nowrap",
                  minWidth: "max-content",
                }}
              >
                {productTabs.map((t) => renderTabButton(t))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bottom row: tabs 11 & 12 unchanged */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        {bottomTabs.map(({ id, label }) => {
          const isEnabled = enabledTabs.includes(id);
          const isActive = id === activeTab;

          return (
            <button
              key={id}
              onMouseEnter={(e) => {
                setShowBubble(true);
                updateBubblePos(e.clientX, e.clientY);
              }}
              onMouseMove={(e) => {
                if (!showBubble) setShowBubble(true);
                updateBubblePos(e.clientX, e.clientY);
              }}
              onMouseLeave={() => setShowBubble(false)}
              onClick={(e) => {
                if (didDragRef.current) {
                  e.preventDefault();
                  e.stopPropagation();
                  return;
                }
                isEnabled && setActiveTab(id);
              }}
              disabled={!isEnabled}
              style={{
                flex: "1 1 auto",
                minWidth: "120px",
                padding: "8px 16px",
                marginRight: "6px",
                marginBottom: "6px",
                fontSize: "2vh",
                fontWeight: isActive ? "600" : "400",
                backgroundColor: isActive ? "white" : "#f1f5f9",
                border: isActive ? "1px solid #cbd5e1" : "none",
                borderBottom: isActive ? "none" : "1px solid #cbd5e1",
                borderTopLeftRadius: `${TAB_RADIUS_PX}px`,
                borderTopRightRadius: `${TAB_RADIUS_PX}px`,
                borderBottomLeftRadius: `${TAB_RADIUS_PX}px`,
                borderBottomRightRadius: `${TAB_RADIUS_PX}px`,
                color: isActive ? "#000" : "#6b7280",
                cursor: isEnabled ? "pointer" : "not-allowed",
                whiteSpace: "nowrap",
              }}
            >
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
